import { demoFunc } from './dees-harness-usage.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { IHarnessUsage } from '../interfaces.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-usage': DeesHarnessUsage;
  }
}

const formatTokenCount = (count?: number): string => {
  if (!count) return '0';
  if (count < 1000) return String(count);
  if (count < 1_000_000) return `${(count / 1000).toFixed(count < 10_000 ? 1 : 0)}k`;
  return `${(count / 1_000_000).toFixed(1)}M`;
};

/**
 * Token-usage chip. Compact mode shows only the total; the full mode shows
 * in/out/total. Cache read/write detail lives in the tooltip.
 */
@customElement('dees-harness-usage')
export class DeesHarnessUsage extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor usage: IHarnessUsage = {};

  @property({ type: Boolean })
  accessor compact: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: inline-block;
        font-family: var(--dees-font-family);
      }

      .chip {
        display: inline-flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        padding: 2px 10px;
        border-radius: var(--dees-radius-full);
        background: var(--dees-color-fill-secondary);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        white-space: nowrap;
        cursor: default;
      }

      .total {
        color: var(--dees-color-text-primary);
        font-weight: 600;
      }
    `,
  ];

  public render(): TemplateResult {
    const usage = this.usage;
    if (!usage.totalTokens && !usage.inputTokens && !usage.outputTokens) return html``;
    const total = usage.totalTokens ?? (usage.inputTokens ?? 0) + (usage.outputTokens ?? 0);
    const tooltip = [
      `input: ${usage.inputTokens ?? 0}`,
      `output: ${usage.outputTokens ?? 0}`,
      `total: ${total}`,
      usage.cacheReadTokens ? `cache read: ${usage.cacheReadTokens}` : '',
      usage.cacheWriteTokens ? `cache write: ${usage.cacheWriteTokens}` : '',
    ].filter(Boolean).join('\n');
    return html`
      <span class="chip" title=${tooltip}>
        ${this.compact
          ? html`<span class="total">${formatTokenCount(total)}</span> tokens`
          : html`
              <span>${formatTokenCount(usage.inputTokens)} in</span>
              <span>·</span>
              <span>${formatTokenCount(usage.outputTokens)} out</span>
              <span>·</span>
              <span class="total">${formatTokenCount(total)}</span>
            `}
      </span>
    `;
  }
}
